import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/services/authService';
import { useAuthStore } from '@/stores/authStore';
import type { MagicLinkRequest, VerifyTokenRequest, User } from '@/types/user';
import type { ApiError } from '@/services/api';

export function useAuth() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    user,
    token,
    isAuthenticated,
    setCredentials,
    logout: storeLogout,
    setUser,
    setLoading,
  } = useAuthStore();

  // Request magic link mutation
  const requestMagicLinkMutation = useMutation({
    mutationFn: (data: MagicLinkRequest) => authService.requestMagicLink(data),
    onError: (error: ApiError) => {
      console.error('Magic link request failed:', error.message);
    },
  });

  // Verify token mutation
  const verifyTokenMutation = useMutation({
    mutationFn: (data: VerifyTokenRequest) => authService.verifyToken(data),
    onSuccess: (response) => {
      setCredentials(response.user, response.accessToken);
      // Also store in localStorage for API interceptor
      localStorage.setItem('auth_token', response.accessToken);
      localStorage.setItem('user', JSON.stringify(response.user));
      navigate('/app/dashboard');
    },
    onError: (error: ApiError) => {
      console.error('Token verification failed:', error.message);
    },
  });

  // Get current user profile query
  const profileQuery = useQuery({
    queryKey: ['profile'],
    queryFn: () => authService.getProfile(),
    enabled: isAuthenticated && !!token,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data: Partial<User>) => authService.updateProfile(data),
    onSuccess: (updatedUser) => {
      setUser(updatedUser);
      queryClient.setQueryData(['profile'], updatedUser);
    },
    onError: (error: ApiError) => {
      console.error('Profile update failed:', error.message);
    },
  });

  // Logout function
  const logout = async () => {
    setLoading(true);
    try {
      await authService.logout();
    } finally {
      storeLogout();
      queryClient.clear();
      setLoading(false);
      navigate('/auth');
    }
  };

  // Request magic link
  const requestMagicLink = async (phone: string) => {
    return requestMagicLinkMutation.mutateAsync({ phoneNumber: phone });
  };

  // Verify token
  const verifyToken = async (verifyToken: string) => {
    return verifyTokenMutation.mutateAsync({ token: verifyToken });
  };

  // Update profile
  const updateProfile = async (data: Partial<User>) => {
    return updateProfileMutation.mutateAsync(data);
  };

  return {
    // State
    user: profileQuery.data || user,
    token,
    isAuthenticated,
    isLoading:
      requestMagicLinkMutation.isPending ||
      verifyTokenMutation.isPending ||
      profileQuery.isLoading,

    // Actions
    requestMagicLink,
    verifyToken,
    updateProfile,
    logout,

    // Mutation states
    requestMagicLinkError: requestMagicLinkMutation.error as ApiError | null,
    verifyTokenError: verifyTokenMutation.error as ApiError | null,
    updateProfileError: updateProfileMutation.error as ApiError | null,
    isRequestingMagicLink: requestMagicLinkMutation.isPending,
    isVerifyingToken: verifyTokenMutation.isPending,
    isUpdatingProfile: updateProfileMutation.isPending,
  };
}

export function useAdminAuth() {
  const navigate = useNavigate();
  const { adminToken, isAdminAuthenticated, setAdminToken, adminLogout: storeAdminLogout } =
    useAuthStore();

  // Validate admin token mutation
  const validateTokenMutation = useMutation({
    mutationFn: (tokenToValidate: string) => authService.validateAdminToken(tokenToValidate),
    onSuccess: (response, token) => {
      if (response.isAdmin) {
        setAdminToken(token);
        localStorage.setItem('admin_token', token);
        navigate('/admin/dashboard');
      } else {
        throw new Error('Invalid admin credentials');
      }
    },
    onError: (error: ApiError) => {
      console.error('Admin validation failed:', error.message);
    },
  });

  // Login with token
  const login = async (token: string) => {
    return validateTokenMutation.mutateAsync(token);
  };

  // Logout
  const logout = () => {
    authService.adminLogout();
    storeAdminLogout();
    navigate('/admin/login');
  };

  return {
    adminToken,
    isAdminAuthenticated,
    isLoading: validateTokenMutation.isPending,
    error: validateTokenMutation.error as ApiError | null,
    login,
    logout,
  };
}

export default useAuth;
